import { Box, Stack, Alert, Typography } from '@mui/material'
import type { UseQueryResult } from '@tanstack/react-query'
import type { ChartRegistryRow } from '~/api/endpoints/getCharts'
import { useDynamicsPanels, type DynamicsWalkFamily } from '~/api/hooks/useDynamicsPanels'
import { MultiTimeframeOverlayChart } from '~/components/Charts/MultiTimeframeOverlayChart'
import { PinButton } from '~/components/CollectionPin/PinButton'
import { ChartCardHeader } from '~/components/Charts/chrome/ChartCardHeader'
import { ChartContextCaption } from '~/components/Charts/chrome/ChartContextCaption'
import { ChartLoadingState } from './ChartLoadingState'
import { FilterApplicabilityNote } from './FilterApplicabilityNote'
import { PendingOverlay } from './PendingOverlay'
import {
  buildChartTitle,
  buildContextCaption,
  timeframePhrase,
  KICKER_BY_CHART_TYPE,
  MESSAGES,
} from '~/components/Charts/chrome/chartChrome'
import { buildPinV1 } from '~/pin/buildPinV1'
import { useMultiTfTimeframes } from './useMultiTfTimeframes'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import { composeChartTitle } from './composeChartTitle'
import type { ReviewPageState } from './useReviewPageState'

interface Props {
  state: ReviewPageState
  charts: UseQueryResult<ChartRegistryRow[], Error>
}

/**
 * Additive multi-timeframe overlay tile (WP-2 slice 4). Renders the MAIN window + the picked
 * timeframes (row 16: main is the first entry) as N index-aligned lines. Appears once >=1
 * timeframe is picked (main + 1 = 2 lines); it sits alongside the per-timeframe panels, never
 * replaces them. Reuses useDynamicsPanels (per-timeframe mode) for the fan-out; bypasses
 * ChartCanvas/Knobs like the other timeframe tiles.
 */
export function MultiTimeframeOverlayTile({ state, charts }: Props) {
  // Hooks first (unconditional), early returns after — hooks-rules.
  const valueLabelFor = useDimValueLabelFor(state.factTable)
  const row = charts.data?.find((r) => r.chart_type === 'multi_timeframe_overlay')
  const family: DynamicsWalkFamily = row?.walk_function === 'walk_ratio' ? 'ratio' : 'timeline'

  // Row 16: [main, ...picked] — 1 pick ⇒ 2 timeframes ⇒ the overlay engages (v2 parity:
  // a single pick is a comparison against the main window, not a lonely line).
  const { timeframes: mtf, pending: mtfPending } = useMultiTfTimeframes(state)
  const timeframes = mtf ?? []

  const data = useDynamicsPanels({
    family,
    kpi: state.kpi,
    factTable: state.factTable,
    // T4b: the main window's walk must use the campaign-merged filter_dims (T2), not the
    // pre-merge state.filters — this is the page fact, already resolved into baseParams.
    filterDims: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    bucketSize: state.baseParams.bucket_size,
    fromIso: state.fromIso,
    toIso: state.toIso,
    unit: state.granularity.unit,
    dots: state.granularity.dots,
    timeframes,
    enabled: Boolean(row) && timeframes.length >= 2 && !mtfPending,
  })

  if (charts.isLoading) return null
  if (!row) return null // additive: no overlay placement → render nothing
  if (timeframes.length < 2) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">{MESSAGES.addTimeframeHint}</Typography>
      </Box>
    )
  }
  // FU-02(b): explicit loading render — the gated (disabled) query reports isLoading:false, so the
  // guard below would fall through to an empty chart. Hold while the ratio-pair machinery settles.
  if (mtfPending) return <ChartLoadingState />
  if (data.isError) {
    return <Alert severity="warning">{MESSAGES.chartDataLoadFailed}</Alert>
  }
  if (data.isLoading) return <ChartLoadingState />

  const series = data.panels.map((p) => ({ id: p.key, label: p.label, points: p.points }))

  const pin = buildPinV1({
    walk_function: family === 'ratio' ? 'walk_ratio' : 'walk_timeline',
    fact_table: state.factTable,
    kpi: state.kpi,
    // FY v2: freeze the RAW display unit (not governance.effectiveUnit) — replay must
    // reproduce what THIS tile folds at (ChartCanvas reads the raw unit), spec §3.2.
    render_unit: state.granularity.unit,
    params: {
      from: state.fromIso,
      to: state.toIso,
      // T4b: freeze exactly the walk's fired filter_dims (baseParams — T2 campaign-merged).
      filter_dims: state.baseParams.filter_dims,
      group_by: state.groupBy,
      bucket_size: state.baseParams.bucket_size,
    },
    chart_type: 'multi_timeframe_overlay',
    // Campaigns C1 Task 8: `timeframes` is the SAME array fed to the live overlay fanout above
    // (row 16/Task 7) — freezing it here carries each arm's resolved filter_dims (when set)
    // straight into the pin for ChartFromPin's MultiTimeframeOverlayFromPin to replay
    // (`tf.filter_dims ?? base`). Do not narrow/remap before assigning.
    // T3-fold: the ONE exception is campaignId — a live, same-session lookup aid (see
    // ResolvedTimeframe's doc comment), never part of the wire shape a saved pin replays
    // (id/label/from/to/filter_dims only) — strip it, everything else passes through verbatim.
    viz_params: { timeframes: timeframes.map(({ campaignId: _campaignId, ...tf }) => tf) },
  })

  // Chrome (rows 4/5/25): cascade-built kpi title (form-consistent with the other tiles, no
  // raw-key flicker while controls load) + ALL rendered timeframe labels (main first, row 16).
  const cardTitle = buildChartTitle({
    kpiTitle: composeChartTitle(state.kpiCascade),
    timeframePhrase: timeframePhrase(timeframes.map((t) => t.label)),
  })
  // T4b: caption mirrors the pin/walk — the actual filter_dims, not the pre-merge state.filters.
  const captionLines = buildContextCaption({
    filters: state.baseParams.filter_dims,
    groupBy: state.groupBy,
    valueLabelFor,
  })

  return (
    <Stack spacing={1}>
      <ChartCardHeader
        kicker={KICKER_BY_CHART_TYPE['multi_timeframe_overlay']!}
        title={cardTitle}
        actions={<PinButton pin={pin} />}
      />
      <PendingOverlay active={data.isPlaceholderData}>
        <MultiTimeframeOverlayChart series={series} groups={data.groups} />
      </PendingOverlay>
      <FilterApplicabilityNote state={state} />
      <ChartContextCaption lines={captionLines} />
    </Stack>
  )
}
